import { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Grid } from '@mui/material'

import Button from './Button'
import Dialog from './Dialog'
import GridSelect from './GridSelect'
import GridCheckbox from './GridCheckbox'
import GridInputField from './GridInputField'
import GridAutoComplete from './GridAutoComplete'
import GridLinkHomepage from './GridLinkHomepage'
import GridLinksHomepage from './GridLinksHomepage'
import GridWeeklyTimeSlot from './GridWeeklyTimeSlot'
import GridCircularProgress from './GridCircularProgress'
import GridWeeklyTimeSlotChoice from './GridWeeklyTimeSlotChoice'
import DialogImpostazioniGoogleMapsTest from './DialogImpostazioniGoogleMapsTest'
import {
  useGetImpostazioniAvanzateQuery,
  useTestGoogleMapsMutation,
} from '../hooks/api-slice'
import { useFeedback } from '../hooks/feedback-hook'
import { formatBodyField } from '../hooks/utils-functions'
import { setImpostazioniAvanzate } from '../container/home'
import { config } from '../settings/configs'
import {
  titlesImpostazioni,
  OPZIONI_ISCRIZIONE,
  TIPOLOGIE_SPOSTAMENTO,
  SQUADRA_INVIO_RISULTATO,
} from '../settings/settings'

const DialogImpostazioniAvanzate = ({ open, handleClose, triggerFunction }) => {
  const avanzate = useSelector(state => state.home.impostazioniAvanzate)
  const { data: impoAvanzate, isLoading } = useGetImpostazioniAvanzateQuery()

  const [testGoogleMapsMutation] = useTestGoogleMapsMutation()

  const dispatch = useDispatch()
  const { setFeedback } = useFeedback()

  const [openTest, setOpenTest] = useState()
  const handleClickOpenTest = () => setOpenTest(true)
  const handleCloseTest = () => setOpenTest()

  const confirmTest = async (origin, destination) => {
    const body = { origin, destination }
    const result = await testGoogleMapsMutation(body)
    if (!result?.error) {
      handleCloseTest()
      setFeedback(
        `Distanza da ${origin} a ${destination}: ${result?.data?.distance} km`,
        false,
        titlesImpostazioni.titleGoogleMapsTest,
      )
    }
  }

  useEffect(() => {
    if (open) dispatch(setImpostazioniAvanzate(impoAvanzate))
  }, [dispatch, impoAvanzate, open])

  const cDispatch = useCallback(
    (field, value) =>
      dispatch(
        setImpostazioniAvanzate({
          ...avanzate,
          [field]: formatBodyField(value),
        }),
      ),
    [dispatch, avanzate],
  )

  const MyGridCheckbox = ({ label, field }) => (
    <GridCheckbox
      label={label}
      field={field}
      value={avanzate?.[field]}
      onChange={cDispatch}
      md={12}
      justifyContent='flex-start'
    />
  )

  const MyGridSelect = ({ label, field, options, optionLabels }) => (
    <GridSelect
      label={label}
      field={field}
      value={avanzate?.[field]}
      options={options}
      optionLabels={optionLabels}
      onChange={cDispatch}
    />
  )

  const MyGridInputField = (label, field, type = 'number', md) => (
    <GridInputField
      label={label}
      field={field}
      value={avanzate?.[field]}
      onChange={cDispatch}
      type={type}
      md={md}
    />
  )

  const MyGridAutoComplete = ({ label, field, options, getOptionLabel }) => (
    <GridAutoComplete
      label={label}
      field={field}
      value={avanzate?.[field]}
      options={options}
      getOptionLabel={getOptionLabel}
      onChange={cDispatch}
      md={12}
      blurOnSelect={false}
      multiple
    />
  )

  const MyTitle = ({ title }) => (
    <Grid size={12} marginTop={2} marginBottom={1}>
      <b>{title}</b>
    </Grid>
  )

  useEffect(() => {
    if (!open) dispatch(setImpostazioniAvanzate())
  }, [dispatch, open])

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      triggerFunction={triggerFunction}
      title={titlesImpostazioni.titleAvanzate}
      textConfirm='Salva'
      maxWidth='md'
    >
      {(isLoading && <GridCircularProgress />) || (
        <>
          <Grid container spacing={3} sx={{ width: '100%' }}>
            <Grid container spacing={1} sx={{ width: '100%' }}>
              <MyTitle title='Link in homepage' />
              <Grid container spacing={0} sx={{ width: '100%' }}>
                {avanzate?.LinksHomepage?.map((x, index) => (
                  <GridLinksHomepage
                    key={index}
                    index={index}
                    x={x}
                    values={avanzate?.LinksHomepage}
                    onChange={x => cDispatch('LinksHomepage', x)}
                  />
                ))}
              </Grid>
              <GridLinkHomepage
                values={avanzate?.LinksHomepage}
                onChange={x => cDispatch('LinksHomepage', x)}
              />
            </Grid>
            {config?.features?.iscrizioni && (
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <MyTitle title='Iscrizioni' />
                <MyGridAutoComplete
                  label='Opzioni di iscrizione'
                  field='OpzioniIscrizione'
                  options={OPZIONI_ISCRIZIONE}
                />
                {avanzate?.OpzioniIscrizione?.includes('modulo-privacy') &&
                  MyGridInputField(
                    'Link modulo privacy',
                    'LinkInfoPrivacy',
                    'url',
                    12,
                  )}
                {avanzate?.OpzioniIscrizione?.includes('regolamento') &&
                  MyGridInputField(
                    'Link regolamento',
                    'LinkRegolamento',
                    'url',
                    12,
                  )}
              </Grid>
            )}
            {config?.features?.spostamenti && (
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <MyTitle title='Spostamenti' />
                <MyGridAutoComplete
                  label='Tipologie di spostamento'
                  field='TipologieSpostamento'
                  options={Object.keys(TIPOLOGIE_SPOSTAMENTO)}
                  getOptionLabel={x => TIPOLOGIE_SPOSTAMENTO[x]?.label}
                />
                {MyGridInputField(
                  'Ore di differenza per spostamento gratuito',
                  'OreDiffSpostamentoGratuito',
                )}
                {MyGridInputField(
                  'Giorni di anticipo per chiedere spostamento',
                  'GiorniAnticipoSpostamento',
                )}
                {MyGridInputField(
                  'Giorni ogni quanto mandare promemoria',
                  'GiorniPromemoriaSpostamento',
                )}
              </Grid>
            )}
            {config?.features?.arbitraggi && (
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <MyTitle title='Arbitraggi' />
                {MyGridInputField(
                  'Giorni di anticipo per chiedere arbitraggio',
                  'GiorniAnticipoArbitraggio',
                )}
              </Grid>
            )}
            {config?.features?.calendari && (
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <MyTitle title='Invio risultati' />
                <MyGridSelect
                  label='Squadra che deve inviare il risultato'
                  field='SquadraInvioRisultato'
                  options={Object.keys(SQUADRA_INVIO_RISULTATO)}
                  optionLabels={Object.values(
                    SQUADRA_INVIO_RISULTATO ?? {},
                  )?.map(x => x?.label)}
                />
                <MyGridSelect
                  label='Squadra che deve inviare il risultato nelle fasi finali'
                  field='SquadraInvioRisultatoFinali'
                  options={Object.keys(SQUADRA_INVIO_RISULTATO)}
                  optionLabels={Object.values(
                    SQUADRA_INVIO_RISULTATO ?? {},
                  )?.map(x => x?.label)}
                />
              </Grid>
            )}
            {config?.features?.iscrizioni && (
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <MyTitle title='Orari di disputa delle gare' />
                <Grid container spacing={0} sx={{ width: '100%' }}>
                  {avanzate?.OrariPartite?.map((x, index) => (
                    <GridWeeklyTimeSlot
                      key={index}
                      index={index}
                      x={x}
                      values={avanzate?.OrariPartite}
                      onChange={x => cDispatch('OrariPartite', x)}
                    />
                  ))}
                </Grid>
                <GridWeeklyTimeSlotChoice
                  values={avanzate?.OrariPartite}
                  onChange={x => cDispatch('OrariPartite', x)}
                />
              </Grid>
            )}
            {config?.features?.classifiche && (
              <Grid container spacing={0} sx={{ width: '100%' }}>
                <MyTitle title='Classifiche' />
                <MyGridCheckbox
                  label='Punto partecipazione Mini'
                  field='PuntoPartecipazioneMini'
                />
                <MyGridCheckbox
                  label='Punto partecipazione Propaganda'
                  field='PuntoPartecipazionePropaganda'
                />
                <MyGridCheckbox
                  label='Ignora risultati con squadre fuori classifica'
                  field='IgnoraRisultatiSquadreFuoriClassifica'
                />
              </Grid>
            )}
            {config?.features?.provvedimenti && (
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <MyTitle title='Provvedimenti' />
                {MyGridInputField(
                  'Giorni ritardo risultati',
                  'GiorniRitardoRisultati',
                )}
                {MyGridInputField(
                  'Giorni dopo quanto mandare promemoria risultato',
                  'GiorniPromemoriaRisultato',
                )}
                {MyGridInputField(
                  'Giorni ritardo referti',
                  'GiorniRitardoReferti',
                )}
                {MyGridInputField(
                  'Giorni dopo quanto mandare promemoria referto',
                  'GiorniPromemoriaReferto',
                )}
              </Grid>
            )}
            {config?.features?.designazioni && (
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <MyTitle title='Designazioni' />
                {MyGridInputField(
                  'Giorni ogni quanto mandare promemoria',
                  'GiorniPromemoriaDesignazione',
                )}
              </Grid>
            )}
            {config?.features?.rimborsi && (
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <MyTitle title='Rimborsi' />
                {MyGridInputField('Costo diaria arbitri', 'CostoDiariaArbitri')}
                <MyGridCheckbox
                  label='Usa Google Maps per km rimborsi'
                  field='GoogleMapsPerRimborsi'
                />
                {avanzate?.GoogleMapsPerRimborsi &&
                  MyGridInputField(
                    'Chiave per API Google Maps',
                    'GoogleMapsApiKey',
                    'text',
                    12,
                  )}
                {avanzate?.GoogleMapsPerRimborsi && (
                  <Grid size={12}>
                    <center>
                      <Button onClick={handleClickOpenTest}>Test API</Button>
                    </center>
                  </Grid>
                )}
              </Grid>
            )}
            {config?.features?.rendiconti && (
              <Grid container spacing={1} sx={{ width: '100%' }}>
                <MyTitle title='Rendiconti' />
                {MyGridInputField(
                  'Costo quota segreteria',
                  'CostoQuotaSegreteria',
                )}
                {MyGridInputField('Costo spostamento', 'CostoSpostamento')}
                {MyGridInputField('Costo tasse gara', 'CostoNeedsReferee')}
                {MyGridInputField(
                  'Costo richieste arbitraggio',
                  'CostoRequiresReferee',
                )}
                {MyGridInputField(
                  'Costo risultati ritardo',
                  'CostoRisultatoRitardo',
                )}
                <MyGridCheckbox
                  label='Impedisci iscrizioni alle società in debito'
                  field='NoIscrizioniDebitori'
                />
                {MyGridInputField(
                  'IBAN rendiconti',
                  'RendicontiIban',
                  'text',
                  12,
                )}
                {MyGridInputField(
                  'Beneficiario rendiconti',
                  'RendicontiBeneficiario',
                  'text',
                  12,
                )}
              </Grid>
            )}
          </Grid>
          <DialogImpostazioniGoogleMapsTest
            open={openTest}
            handleClose={handleCloseTest}
            triggerFunction={confirmTest}
          />
        </>
      )}
    </Dialog>
  )
}

export default DialogImpostazioniAvanzate
